'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { adminContentService, ManagedPressRelease } from '@/lib/services/adminContentService';

const PressReleaseSection: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [pressReleases, setPressReleases] = useState<ManagedPressRelease[]>([]);
  const [loading, setLoading] = useState(true);

  const categories = ['All', 'Announcements', 'Impact Reports', 'Partnerships', 'Events', 'Research'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await adminContentService.getPressReleases();
      setPressReleases(data.filter(pr => pr.is_visible));
    } catch (e) {
      console.warn('Failed to load press releases');
    } finally {
      setLoading(false);
    }
  };

  const filteredReleases = pressReleases.filter(release => {
    const matchesCategory = selectedCategory === 'All' || release.category === selectedCategory;
    const matchesSearch = searchQuery === '' || release.title.toLowerCase().includes(searchQuery.toLowerCase()) || release.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-muted rounded w-1/3 mx-auto" />
            {[1, 2, 3].map(i => <div key={i} className="h-40 bg-muted rounded" />)}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-6 py-3 rounded-full mb-6">
            <Icon name="NewspaperIcon" size={24} className="text-primary" />
            <span className="text-primary font-cta text-sm font-bold">Press Release Archive</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">Latest News & Announcements</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-body">
            Stay informed about SIG Day (The Global No Enemy Festival) milestones, research findings, and global impact through our official press releases.
          </p>
        </div>

        <div className="mb-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-8">
            <div className="relative w-full lg:w-96">
              <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input type="text" placeholder="Search press releases..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {categories.map(category => (
                <button key={category} onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-full font-cta text-sm font-bold transition-all duration-300 ${selectedCategory === category ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground border border-border hover:border-primary'}`}>
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {filteredReleases.map(release => (
              <div key={release.id} className="bg-card rounded-lg p-6 lg:p-8 border border-border transition-all duration-300 hover:shadow-lg hover:border-primary group">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-xs font-cta font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">{release.category}</span>
                      <span className="text-sm text-muted-foreground font-body">{release.date}</span>
                      <span className="w-1 h-1 bg-muted-foreground rounded-full" />
                      <span className="text-sm text-muted-foreground font-body">{release.read_time}</span>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-headline font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">{release.title}</h3>
                    <p className="text-muted-foreground font-body leading-relaxed">{release.excerpt}</p>
                  </div>
                  <button className="flex items-center space-x-2 text-primary font-cta text-sm font-bold transition-all duration-300 hover:text-peaceful-teal whitespace-nowrap">
                    <span>Read Full Release</span>
                    <Icon name="ArrowRightIcon" size={18} />
                  </button>
                </div>
                <div className="flex items-center space-x-4 pt-4 border-t border-border">
                  {release.pdf_url && (
                    <a href={release.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-muted-foreground font-body text-sm transition-colors duration-300 hover:text-primary">
                      <Icon name="ArrowDownTrayIcon" size={16} />
                      <span>Download PDF</span>
                    </a>
                  )}
                  <button className="flex items-center space-x-2 text-muted-foreground font-body text-sm transition-colors duration-300 hover:text-primary">
                    <Icon name="ShareIcon" size={16} />
                    <span>Share</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredReleases.length === 0 && (
            <div className="text-center py-16">
              <Icon name="DocumentTextIcon" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground font-body">No press releases found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PressReleaseSection;